<template>
  <div class="group relative flex flex-col">
    <label
      :for="name"
      class="text-sm text-gray-300">
      {{ label }}{{ required ? '*' : '' }}
    </label>
    <input
      :id="name"
      ref="input"
      :label="label"
      :name="name"
      :type="type"
      :min="min"
      :max="max"
      :minlength="minlength"
      :maxlength="maxlength"
      :inputmode="inputmode"
      :pattern="pattern"
      :title="title"
      :required="required"
      :placeholder="placeholder"
      :value="modelValue"
      class="mt-1.5 h-14 rounded-full border-2 border-gray-200 bg-white px-6 py-3 disabled:cursor-wait"
      :class="[modelValue ? 'text-gray-500' : 'text-sm text-gray-250', wasInvalidated ? 'invalid:border-red-400' : '']"
      :disabled="state === FormState.LOADING"
      @input="onChange"
      @invalid="wasInvalidated = true" />
    <span
      v-if="errorMessage && wasInvalidated && !isValid"
      class="absolute bottom-0 -mb-5 text-xs text-red-400">
      {{ errorMessage }}
    </span>
  </div>
</template>

<script setup lang="ts">
  import { onMounted, ref } from '#imports'

  import { FormState } from '~/constants/form'

  type InputProps = {
    state: FormState
    label: string
    type: string
    name: string
    modelValue: string | Date | null
    placeholder: string
    min?: string
    max?: string
    minlength?: number
    maxlength?: number
    inputmode?: 'text' | 'none' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search'
    pattern?: string
    title?: string
    required?: boolean
    errorMessage?: string
  }

  type InputEmits = {
    (event: 'update:modelValue', value: string): void
  }

  defineProps<InputProps>()

  const emit = defineEmits<InputEmits>()

  // eslint-disable-next-line vue/prefer-use-template-ref
  const input = ref<HTMLInputElement | null>(null)
  const wasInvalidated = ref(false)
  const isValid = ref<boolean | null>(null)

  function onChange(event: Event) {
    emit('update:modelValue', (event.target as HTMLInputElement).value)
    wasInvalidated.value = false
    isValid.value = input.value.validity.valid
  }

  onMounted(() => {
    isValid.value = input.value.validity.valid
  })
</script>

<style scoped>
  input::placeholder {
    @apply text-gray-250;
  }
</style>
